/**
 * Enrich Nostr context chips / project items with profile pics, banners, snippets.
 */

import type { NostrContextChip, NostrContextMeta, NostrContextPack } from '@/lib/workspace/types';
import { getProfileCached } from '@/lib/profileCache';
import {
  fetchNostrEntity,
  parseNostrRef,
  type NostrQueryFn,
} from './fetchEntity';
import { expandNostrPack, npubOf } from './packs';
import { verifyNip05ForPubkey } from './mentions';

function shortRef(ref: string, n = 18): string {
  const s = ref.replace(/\s+/g, '');
  if (s.length <= n * 2) return s;
  return `${s.slice(0, n)}…${s.slice(-8)}`;
}

function snippetFromText(text: string, max = 160): string | undefined {
  // Prefer about / body after metadata lines
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const skip = /^(###|npub:|pubkey:|id:|author:|kind:|created_at:|tags:|nip05:|lightning:|website:|picture:|image:|name:|handle:|summary:|d:)/i;
  const body: string[] = [];
  for (const line of lines) {
    if (skip.test(line)) continue;
    if (line.startsWith('---')) continue;
    body.push(line);
    if (body.join(' ').length > max) break;
  }
  const s = body.join(' ').replace(/\s+/g, ' ').trim();
  if (!s) return undefined;
  return s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s;
}

function aboutFromProfileText(text: string): string | undefined {
  // formatProfileRich puts about after blank line at end
  const parts = text.split(/\n\n+/);
  if (parts.length < 2) return undefined;
  const last = parts[parts.length - 1]?.trim();
  if (!last || last.startsWith('###') || last.startsWith('npub:')) return undefined;
  return last.length > 200 ? last.slice(0, 199) + '…' : last;
}

/**
 * Build rich NostrContextMeta from a chip (after optional live fetch).
 */
export async function enrichChipToMeta(
  chip: NostrContextChip,
  query: NostrQueryFn,
  options?: { signal?: AbortSignal; selfPubkey?: string },
): Promise<NostrContextMeta> {
  const pack = chip.pack;
  const ref = chip.ref.replace(/\s+/g, '');
  const parsed = parseNostrRef(ref);

  // Network-search is a live recipe, not a fetchable entity. Never try to
  // resolve its sentinel ref; the query text lives in chip.snippet and the
  // pack expands at send time with keywords from the user's message.
  if (pack === 'network-search') {
    return {
      ref,
      entity: 'pack',
      pack,
      displayName: chip.title || 'Nostr · my follows',
      snippet: chip.snippet,
      noteLimit: chip.noteLimit,
      cachedAt: Date.now(),
    };
  }

  let entity: NostrContextMeta['entity'] = 'event';
  if (
    pack === 'my-profile' ||
    pack === 'my-recent' ||
    pack === 'profile-brief' ||
    pack === 'profile-recent'
  ) {
    entity = 'profile';
  } else if (pack === 'thread') {
    entity = 'thread';
  } else if (pack === 'article' || (parsed?.kind === 'naddr' && parsed.eventKind === 30023)) {
    entity = 'addr';
  } else if (parsed?.kind === 'naddr') {
    entity = 'addr';
  } else if (parsed?.kind === 'npub' || parsed?.kind === 'nprofile') {
    entity = 'profile';
  }

  const meta: NostrContextMeta = {
    ref,
    entity,
    pack,
    authorPubkey: chip.pubkey,
    displayName: chip.title,
    nip05: chip.nip05,
    picture: chip.picture,
    image: chip.image,
    snippet: chip.snippet,
    noteLimit: chip.noteLimit,
    cachedText: chip.cachedText,
  };

  // Seed from profile cache
  if (chip.pubkey) {
    const cached = getProfileCached(chip.pubkey);
    const m = cached?.metadata;
    if (m) {
      if (!meta.picture && typeof m.picture === 'string') meta.picture = m.picture;
      if (!meta.image && typeof m.banner === 'string') meta.image = m.banner;
      if (!meta.nip05 && typeof m.nip05 === 'string') meta.nip05 = m.nip05;
      if (!meta.name && typeof m.name === 'string') meta.name = m.name;
      if (
        (!meta.displayName || meta.displayName === shortRef(ref)) &&
        (typeof m.display_name === 'string' || typeof m.name === 'string')
      ) {
        meta.displayName =
          (typeof m.display_name === 'string' && m.display_name) ||
          (typeof m.name === 'string' ? m.name : meta.displayName);
      }
      if (!meta.about && typeof m.about === 'string') {
        meta.about =
          m.about.length > 200 ? m.about.slice(0, 199) + '…' : m.about;
      }
    }
  }

  // Live enrich when we lack visuals or body
  const needsFetch =
    !meta.picture ||
    !meta.cachedText ||
    (entity === 'addr' && !meta.image) ||
    (entity === 'profile' && !meta.about);

  if (!needsFetch && meta.picture) {
    meta.cachedAt = Date.now();
    return meta;
  }

  try {
    if (entity === 'profile' || pack === 'my-profile' || pack === 'profile-brief') {
      const pubkey = chip.pubkey || options?.selfPubkey;
      if (pubkey) {
        const r = await fetchNostrEntity(npubOf(pubkey), query, {
          signal: options?.signal,
          includeAuthor: false,
        });
        if (r.ok) {
          meta.cachedText = r.text;
          meta.cachedAt = Date.now();
          if (r.meta?.picture) meta.picture = r.meta.picture;
          if (r.meta?.image) meta.image = r.meta.image;
          if (r.meta?.nip05) meta.nip05 = r.meta.nip05;
          if (r.meta?.displayName) meta.displayName = r.meta.displayName;
          const about = aboutFromProfileText(r.text);
          if (about) meta.about = about;
          const bannerLine = r.text.match(/^banner: (.+)$/m)?.[1]?.trim();
          if (bannerLine && !meta.image) meta.image = bannerLine;
          // Banner from kind-0 JSON if present in cache after setProfileCached
          const c2 = getProfileCached(pubkey);
          const bm = c2?.metadata as Record<string, unknown> | undefined;
          if (typeof bm?.banner === 'string' && !meta.image) meta.image = bm.banner;
          if (typeof bm?.name === 'string') meta.name = bm.name;
        }
      }
    } else if (pack === 'my-recent' || pack === 'profile-recent') {
      const pubkey = chip.pubkey || options?.selfPubkey;
      if (pubkey) {
        const result = await expandNostrPack(pack, {
          query,
          pubkey,
          noteLimit: chip.noteLimit,
          signal: options?.signal,
          label: chip.title,
        });
        if (result.ok) {
          meta.cachedText = result.text;
          meta.cachedAt = Date.now();
          meta.snippet = snippetFromText(result.text, 180);
          const c2 = getProfileCached(pubkey);
          const bm = c2?.metadata as Record<string, unknown> | undefined;
          if (typeof bm?.picture === 'string') meta.picture = bm.picture;
          if (typeof bm?.banner === 'string') meta.image = bm.banner;
          if (typeof bm?.nip05 === 'string') meta.nip05 = bm.nip05;
          if (typeof bm?.about === 'string') {
            const a = bm.about;
            meta.about = a.length > 200 ? a.slice(0, 199) + '…' : a;
          }
          if (typeof bm?.display_name === 'string') meta.displayName = bm.display_name;
          else if (typeof bm?.name === 'string') meta.displayName = bm.name;
          if (typeof bm?.name === 'string') meta.name = bm.name;
        }
      }
    } else {
      // event / article / thread
      const r = await fetchNostrEntity(ref, query, {
        signal: options?.signal,
        includeAuthor: true,
        timeoutMs: parsed?.kind === 'naddr' ? 14_000 : 10_000,
      });
      if (r.ok) {
        meta.cachedText = r.text;
        meta.cachedAt = Date.now();
        meta.authorPubkey = r.pubkey || meta.authorPubkey;
        if (r.meta?.title) meta.displayName = r.meta.title;
        if (r.meta?.image) meta.image = r.meta.image;
        if (r.meta?.picture) meta.picture = r.meta.picture;
        if (r.meta?.eventKind != null) meta.eventKind = r.meta.eventKind;
        if (r.meta?.eventKind === 30023) {
          meta.entity = 'addr';
          meta.pack = 'article';
        }
        meta.snippet = snippetFromText(r.text, 180);
        // summary line
        const sum = r.text.match(/^summary: (.+)$/m)?.[1]?.trim();
        if (sum) meta.about = sum.length > 200 ? sum.slice(0, 199) + '…' : sum;
      }
    }
  } catch {
    // keep partial meta
  }

  // Verify declared NIP-05 once (forward-resolve → compare pubkey). Result
  // is cached on the item so cards/typeahead can show verified state.
  if (meta.nip05 && meta.authorPubkey && meta.nip05Verified === undefined) {
    try {
      meta.nip05Verified = await verifyNip05ForPubkey(
        meta.nip05,
        meta.authorPubkey,
        { signal: options?.signal },
      );
    } catch {
      /* leave undefined */
    }
  }

  return meta;
}

/** Convert chip → project ContextItem fields (caller assigns id). */
export async function chipToContextFields(
  chip: NostrContextChip,
  query: NostrQueryFn,
  options?: { signal?: AbortSignal; selfPubkey?: string },
): Promise<{
  type: 'nostr';
  title: string;
  content: string;
  meta: NostrContextMeta;
}> {
  const meta = await enrichChipToMeta(chip, query, options);
  const title =
    meta.displayName?.trim() ||
    chip.title ||
    shortRef(chip.ref);
  return {
    type: 'nostr',
    title,
    content: meta.ref || chip.ref,
    meta,
  };
}

export function packLabel(pack?: NostrContextPack, entity?: string): string {
  switch (pack) {
    case 'my-profile':
      return 'My profile';
    case 'my-recent':
      return 'My recent notes';
    case 'profile-brief':
      return 'Profile';
    case 'profile-recent':
      return 'Profile + notes';
    case 'article':
      return 'Article';
    case 'thread':
      return 'Thread';
    case 'single':
      return entity === 'profile' ? 'Profile' : 'Event';
    case 'network-search':
      return 'Follows search';
    default:
      if (entity === 'profile') return 'Profile';
      if (entity === 'addr') return 'Addressable';
      if (entity === 'thread') return 'Thread';
      return 'Nostr';
  }
}
